<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up" style="width:680px">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h2 class="rk-modal-title">{{ existing ? (editable ? 'Update Rock' : 'Rock') : 'Add Rock' }} · {{ quarterLabel(quarter) }}</h2>

            <div class="rk-label">Rock</div>
            <input type="text" v-model="form.Name" :disabled="!editable" :placeholder="'What must be done by ' + shortDate(bounds.end) + '? Specific and measurable.'" ref="nameInput" />

            <div class="rk-row">
                <div>
                    <div class="rk-label">Owner</div>
                    <select v-model="form.OwnerEmployeeID" :disabled="!editable">
                        <option :value="null">No owner</option>
                        <option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option>
                    </select>
                </div>
                <div>
                    <div class="rk-label">Team</div>
                    <select v-model="form.Team" :disabled="!editable">
                        <option v-if="!form.Team" :value="null" disabled>Pick a team</option>
                        <option v-for="t in teamOptions" :key="t" :value="t">{{ teamLabel(t) }}</option>
                    </select>
                </div>
            </div>

            <!-- People who own the rock just as its owner does (rocks.ts): it's on their My rocks and task manager inbox,
                 and they can change it -->
            <div class="rk-label">Additional Owners</div>
            <div class="rk-owners">
                <span v-for="id in form.AdditionalOwnerIDs" :key="id" class="rk-owner-chip">
                    {{ employeeName(id) }}
                    <span v-if="editable" class="rk-owner-x" :title="'Remove ' + employeeName(id)" @click="removeOwner(id)"><svg-icon icon="x" /></span>
                </span>
                <select v-if="editable && ownerChoices.length" v-model="addOwnerID" class="rk-owner-add" aria-label="Add an owner" @change="addOwner()">
                    <option :value="null">+ Add Owner</option>
                    <option v-for="e in ownerChoices" :key="e.ID" :value="e.ID">{{ e.Name }}</option>
                </select>
                <span v-if="!editable && !form.AdditionalOwnerIDs.length" class="rk-hint">None</span>
            </div>

            <!-- Teams the rock is on just as it's on its team (rocks.ts): it shows on their tab, their meetings' Rocks
                 panel and their Forge prep, and their members can see it -->
            <div class="rk-label">Additional Teams</div>
            <div class="rk-owners">
                <span v-for="t in form.AdditionalTeams" :key="t" class="rk-owner-chip">
                    {{ teamLabel(t) }}
                    <span v-if="editable" class="rk-owner-x" :title="'Remove ' + teamLabel(t)" @click="removeTeam(t)"><svg-icon icon="x" /></span>
                </span>
                <select v-if="editable && teamChoices.length" v-model="addTeamName" class="rk-owner-add" aria-label="Add a team" @change="addTeam()">
                    <option :value="null">+ Add Team</option>
                    <option v-for="t in teamChoices" :key="t" :value="t">{{ teamLabel(t) }}</option>
                </select>
                <span v-if="!editable && !form.AdditionalTeams.length" class="rk-hint">None</span>
            </div>

            <!-- The whole team's rock rather than just the owner's: listed first, under Team Rocks (RocksView.vue) -->
            <label class="rk-teamrock" :class="[ form.TeamRock ? 'on' : '', !editable ? 'disabled' : '' ]">
                <input type="checkbox" v-model="form.TeamRock" :disabled="!editable" />
                <span class="rk-teamrock-text">
                    <span class="rk-teamrock-title">Team Rock</span>
                    <span class="rk-hint">The whole team works on it, not just the owner. These are visible to the whole company.</span>
                </span>
            </label>

            <!-- What finished looks like, so everyone agrees when it's done -->
            <div class="rk-label">Done</div>
            <textarea v-model="form.DoneStatement" :disabled="!editable" rows="2" maxlength="1000" class="rk-done" placeholder="A short statement describing the end state of this rock, e.g. &quot;New quoting process live, used for every quote, with average turnaround under 2 days.&quot;"></textarea>

            <div class="rk-label">Status</div>
            <div class="rk-seg">
                <button v-for="s in statusOptions" :key="s.value" type="button" :class="form.Status == s.value ? 'on' : ''" :disabled="!editable && !updatable" @click="setStatus(s.value)">{{ s.label }}</button>
            </div>

            <div class="rk-label">Milestones <span class="rk-hint">{{ doneCount }} of {{ form.Milestones.length }} done · due by {{ shortDate(bounds.end) }}</span></div>
            <!-- Dragged by their handle only (as on deck card checklists), so the fields stay editable; the order saves with the form -->
            <draggable :list="form.Milestones" handle=".crm-checklist-handle" :disabled="!editable" :animation="150" ghost-class="kanban-ghost">
                <div v-for="(m, i) in form.Milestones" :key="milestoneKey(m)">
                    <div class="rk-ms" :class="[ m.Done ? 'done' : '', editable ? 'rk-ms-drag' : '' ]">
                        <span v-if="editable" class="crm-checklist-handle" title="Drag to reorder" aria-hidden="true">&#8942;&#8942;</span>
                        <input type="checkbox" v-model="m.Done" :disabled="!canCheck(m)" :aria-label="m.Done ? 'Mark not done' : 'Mark done'" @change="checkChange(m)" />
                        <input type="text" v-model="m.Text" :disabled="!editable" placeholder="Milestone" class="rk-ms-text" />
                        <select v-model="m.OwnerEmployeeID" :disabled="!editable" class="rk-ms-own" aria-label="Milestone owner">
                            <option :value="null">{{ form.OwnerEmployeeID ? 'Rock owner' : 'No owner' }}</option>
                            <option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option>
                        </select>
                        <input type="date" v-model="m.DueDate" :disabled="!editable" :min="bounds.start" :max="bounds.end" class="rk-ms-date" aria-label="Due date" />
                        <span class="rk-ms-wk">{{ m.DueDate ? 'wk ' + weekOfQuarter(quarter, m.DueDate) : '' }}</span>
                        <!-- Optional link: editors open its field below the row; everyone else opens the link itself -->
                        <span v-if="editable" class="rk-ms-ln" :class="m.Link ? 'on' : ''" :title="m.Link ? 'Edit link' : 'Add link'" @click="toggleLink(m)"><svg-icon icon="link" /></span>
                        <a v-else-if="m.Link" class="rk-ms-ln on" :href="m.Link" target="_blank" rel="noopener" :title="milestoneFileName(m.Link) || 'Open link'"><svg-icon icon="link" /></a>
                        <span v-else class="rk-ms-ln"></span>
                        <span v-if="editable" class="rk-ms-x" title="Remove milestone" @click="form.Milestones.splice(i, 1)"><svg-icon icon="x" /></span>
                    </div>
                    <!-- One link per milestone: pasted, or an uploaded file (which becomes the link) -->
                    <div v-if="editable && linkOpen.includes(m)" class="rk-ms-link">
                        <template v-if="milestoneFileName(m.Link)">
                            <a class="rk-ms-file" :href="m.Link" target="_blank" rel="noopener" :title="milestoneFileName(m.Link)"><svg-icon icon="link" /><span>{{ milestoneFileName(m.Link) }}</span></a>
                            <a class="rk-link" @click="m.Link = ''">Remove</a>
                        </template>
                        <template v-else>
                            <input type="text" v-model="m.Link" :disabled="uploading.includes(m)" placeholder="Paste a link (optional)" aria-label="Milestone link" />
                            <a v-if="m.Link" class="rk-link" :href="linkHref(m.Link)" target="_blank" rel="noopener">Open</a>
                        </template>
                        <span v-if="uploading.includes(m)" class="rk-hint">Uploading…</span>
                        <a v-else class="rk-link" @click="pickFile(m)">{{ milestoneFileName(m.Link) ? 'Replace File' : 'Upload File' }}</a>
                    </div>
                </div>
            </draggable>
            <input ref="fileInput" type="file" style="display:none" @change="uploadFile" />
            <p v-if="!form.Milestones.length" class="rk-hint" style="margin:0 0 6px">Break the rock into 3 to 7 milestones, one every 2 to 4 weeks. Each can have its own owner.</p>
            <div v-if="editable" class="rk-add-ms" @click="addMilestone()">+ Add Milestone</div>
            <!-- The Forge, for this rock's milestones only (ForgeRockMilestones.vue): drafts go onto the form above, Save keeps them -->
            <forge-rock-milestones v-if="editable" :rockID="existing ? existing.ID : 0" :form="form" :quarter="quarter" :employees="employees" @add="addDrafted" />

            <div class="rk-actions">
                <!-- The rock's other actions as links on the left (they wrap among themselves); Cancel/Save stay on the right -->
                <div v-if="existing" class="rk-actions-side">
                    <!-- A new rock with everything this one has, milestones included, to change before adding it -->
                    <a v-if="teams.length" class="rk-link" @click.prevent="clone()">Clone</a>
                    <a v-if="updatable && canCarry" class="rk-link" @click.prevent="carry()">Carry to {{ quarterLabel(nextQuarter(quarter)) }}</a>
                    <!-- Opened from its card on a task manager (CompanyBoard.vue): takes the card off, not the rock -->
                    <a v-if="onBoard" class="rk-danger" @click.prevent="$emit('remove-from-board')">Remove from Board</a>
                    <a v-if="editable" class="rk-danger" @click.prevent="showConfirmDelete = true">Delete</a>
                </div>
                <!-- On phones, on a line of their own -->
                <div class="rk-actions-main">
                    <div class="btn-light" @click="$emit('close')">{{ editable ? 'Cancel' : 'Close' }}</div>
                    <div v-if="editable" class="btn" :class="saving ? 'disable-button' : ''" @click="save()">{{ existing ? 'Save' : 'Add Rock' }}</div>
                </div>
            </div>
        </div>

        <confirm-delete v-if="showConfirmDelete" @confirm="remove()" @cancel="showConfirmDelete = false" msg="Delete this rock and its milestones?" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { VueDraggableNext } from 'vue-draggable-next';
import ConfirmDelete from './ConfirmDelete.vue';
import SvgIcon from './SvgIcon.vue';
import ForgeRockMilestones from './ForgeRockMilestones.vue';
import { COMPANY_TEAM } from '@/scoreboard';
import { quarterLabel, quarterBounds, nextQuarter, currentQuarter, weekOfQuarter, shortDate, isRockOwner, milestoneFileName } from '@/rocks';

// Milestone row keys (milestoneKey), by the milestone object
const milestoneKeys = new WeakMap<object, number>();
let lastMilestoneKey = 0;

export default defineComponent({
    name: 'RockModal',
    // rock: the one being viewed/edited, or null to add one to quarter. defaultTeam: for a new rock.
    // teams: the teams you can put a rock on. onBoard: opened from the rock's card on a board.
    props: [ 'rock', 'quarter', 'defaultTeam', 'employees', 'teams', 'myEmployeeID', 'onBoard' ],
    emits: [ 'close', 'saved', 'remove-from-board' ],
    components: {
        draggable: VueDraggableNext,
        ConfirmDelete,
        SvgIcon,
        ForgeRockMilestones
    },
    data() {
        return {
            form: {} as any,
            saving: false,
            showConfirmDelete: false,
            addOwnerID: null as any,
            addTeamName: null as any,
            cloned: false, // Clone turned the form into a new rock
            linkOpen: [] as any[], // milestones whose link field is showing
            uploading: [] as any[], // milestones with a file on its way up
            uploadFor: null as any, // the milestone the file picker is choosing for
            uploadedLinks: [] as string[] // files uploaded on this form since it was last saved
        }
    },
    created() {
        const r = this.rock;
        this.form = {
            Name: r ? r.Name : '',
            DoneStatement: r ? (r.DoneStatement || '') : '',
            TeamRock: r ? !!r.TeamRock : false,
            OwnerEmployeeID: r ? r.OwnerEmployeeID : (this.myEmployeeID || null),
            AdditionalOwnerIDs: r ? [ ...(r.AdditionalOwnerIDs || []) ] : [],
            AdditionalTeams: r ? [ ...(r.AdditionalTeams || []) ] : [],
            Team: r ? (r.Team || null) : (this.defaultTeam || this.teams[0] || null),
            Status: r ? (r.Status == 'notdone' ? 'off' : r.Status) : 'on',
            Milestones: r ? r.Milestones.map((m: any) => ({ ...m })) : []
        };
    },
    mounted() {
        if(!this.rock) {
            (this.$refs.nameInput as HTMLInputElement)?.focus();
        }
    },
    // Closed without saving: the files uploaded on it go (the API keeps any a milestone still links to)
    beforeUnmount() {
        if(this.uploadedLinks.length) {
            axios.post(`${this.$store.state.apiUrl}/api/rocks/milestone/file/discard`, { Links: this.uploadedLinks })
                .catch(e => console.log(e));
        }
    },
    watch: {
        // The rock's team isn't also an additional team
        'form.Team'(team: any) {
            this.form.AdditionalTeams = this.form.AdditionalTeams.filter((t: string) => t != team);
        },
        // Milestones left on the old rock owner follow the rock to its new owner
        'form.OwnerEmployeeID'(owner: any, before: any) {
            // The new owner isn't also an additional owner
            this.form.AdditionalOwnerIDs = this.form.AdditionalOwnerIDs.filter((id: any) => id != owner);
            this.form.Milestones.forEach((m: any) => {
                if(!m.Done && m.OwnerEmployeeID == before) {
                    m.OwnerEmployeeID = owner;
                }
            });
        }
    },
    computed: {
        // Edit: the rock's team (and admins). Update: those, or its owner from another team, who can
        // change its status, check off milestones and carry it forward.
        // Team Hub Admins edit any rock, everyone else only the rocks they own (rocks.ts canEdit; checked here too so
        // the form follows the rule whatever the API says). Anyone can add one.
        // The saved rock the form is for, or null for a new one (including a clone)
        existing(): any {
            return this.cloned ? null : this.rock;
        },
        mayChange(): boolean {
            return this.$store.state.user.teamHubRole == 'Admin' || (!!this.existing && isRockOwner(this.existing, this.myEmployeeID));
        },
        editable(): boolean {
            return !this.existing || (this.existing.CanEdit && this.mayChange);
        },
        updatable(): boolean {
            return !this.existing || (this.existing.CanUpdate && this.mayChange);
        },
        bounds(): { start: string, end: string } {
            return quarterBounds(this.quarter);
        },
        doneCount(): number {
            return this.form.Milestones.filter((m: any) => m.Done).length;
        },
        teamOptions(): string[] {
            return this.existing?.Team && !this.teams.includes(this.existing.Team) ? [ this.existing.Team, ...this.teams ] : this.teams;
        },
        // Who can be added as an additional owner: not the owner, and not already added
        // Teams that can be added: ones you can put a rock on, not its team, not already added. Not Company: everyone
        // already sees it, so it's only ever a rock's main team.
        teamChoices(): string[] {
            return this.teams.filter((t: string) => t != COMPANY_TEAM && t != this.form.Team && !this.form.AdditionalTeams.includes(t));
        },
        ownerChoices(): any[] {
            return (this.employees || []).filter((e: any) => e.ID != this.form.OwnerEmployeeID && !this.form.AdditionalOwnerIDs.includes(e.ID));
        },
        statusOptions(): any[] {
            return [ { value: 'on', label: 'On Track' }, { value: 'off', label: 'Off Track' }, { value: 'done', label: 'Done' } ];
        },
        canCarry(): boolean {
            return this.quarter == currentQuarter() && !this.rock.CarriedToQuarter && this.rock.Status != 'done';
        }
    },
    methods: {
        quarterLabel,
        nextQuarter,
        weekOfQuarter,
        shortDate,
        // Saves with the form when you can edit the rock; otherwise (its owner from another team) right away
        async setStatus(status: string) {
            const before = this.form.Status;
            this.form.Status = status;
            if(this.editable || !this.updatable || status == before) {
                return;
            }
            await axios.post(`${this.$store.state.apiUrl}/api/rocks/rock/status`, { ID: this.rock.ID, Status: status })
                .then(() => {
                    this.$emit('saved', true);
                })
                .catch(e => {
                    console.log(e);
                    this.form.Status = before;
                    alert('There was an error');
                })
        },
        // Milestones check off with the rest of the form when you can edit the rock; the rock's owner, or
        // a milestone's own owner, can check them off straight away
        canCheck(m: any): boolean {
            if(this.editable) {
                return true;
            }
            if(this.updatable && m.ID) {
                return true;
            }
            const owner = m.OwnerEmployeeID || this.existing.OwnerEmployeeID;
            return !!m.ID && !!this.myEmployeeID && owner == this.myEmployeeID;
        },
        async checkChange(m: any) {
            if(this.editable) {
                return;
            }
            await axios.post(`${this.$store.state.apiUrl}/api/rocks/milestone`, { ID: m.ID, Done: m.Done })
                .then(() => {
                    this.$emit('saved', true);
                })
                .catch(e => {
                    console.log(e);
                    m.Done = !m.Done;
                    alert('There was an error');
                })
        },
        employeeName(id: any): string {
            return (this.employees || []).find((e: any) => e.ID == id)?.Name || 'Unknown';
        },
        addOwner() {
            if(this.addOwnerID) {
                this.form.AdditionalOwnerIDs.push(this.addOwnerID);
            }
            this.addOwnerID = null;
        },
        addTeam() {
            if(this.addTeamName) {
                this.form.AdditionalTeams.push(this.addTeamName);
            }
            this.addTeamName = null;
        },
        removeTeam(team: string) {
            this.form.AdditionalTeams = this.form.AdditionalTeams.filter((t: string) => t != team);
        },
        removeOwner(id: any) {
            this.form.AdditionalOwnerIDs = this.form.AdditionalOwnerIDs.filter((x: any) => x != id);
        },
        addMilestone() {
            // Defaults a couple of weeks after the last one (or today, if later), within the quarter
            const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const last = this.form.Milestones[this.form.Milestones.length - 1];
            const today = ymd(new Date());
            let due = [ last?.DueDate || '', today, this.bounds.start ].reduce((a, b) => a > b ? a : b);
            const d = new Date(due + 'T00:00:00');
            d.setDate(d.getDate() + 14);
            const next = ymd(d);
            due = next > this.bounds.end ? this.bounds.end : next;
            this.form.Milestones.push({ Text: '', OwnerEmployeeID: this.form.OwnerEmployeeID || null, DueDate: due, Done: false });
            // Ready to type: focus the new milestone's text once its row is on the page
            this.$nextTick(() => {
                const inputs = (this.$el as HTMLElement).querySelectorAll<HTMLInputElement>('.rk-ms-text');
                inputs[inputs.length - 1]?.focus();
            });
        },
        toggleLink(m: any) {
            const at = this.linkOpen.indexOf(m);
            if(at > -1) {
                this.linkOpen.splice(at, 1);
                return;
            }
            this.linkOpen.push(m);
            this.$nextTick(() => {
                const rows = (this.$el as HTMLElement).querySelectorAll<HTMLElement>('.rk-ms-link');
                // The open link fields are in milestone order (an uploaded file's has no text box)
                rows[this.form.Milestones.filter((x: any) => this.linkOpen.includes(x)).indexOf(m)]?.querySelector<HTMLInputElement>('input')?.focus();
            });
        },
        milestoneFileName,
        // A key that follows the milestone when it's dragged: new ones have no ID yet, so each object gets its own
        milestoneKey(m: any): number {
            if(!milestoneKeys.has(m)) {
                milestoneKeys.set(m, ++lastMilestoneKey);
            }
            return milestoneKeys.get(m) as number;
        },
        pickFile(m: any) {
            this.uploadFor = m;
            const input = this.$refs.fileInput as HTMLInputElement;
            input.value = '';
            input.click();
        },
        // The file goes up now and its link replaces the milestone's; saving the rock keeps it
        async uploadFile(e: Event) {
            const file = (e.target as HTMLInputElement).files?.[0];
            const m = this.uploadFor;
            this.uploadFor = null;
            if(!file || !m) {
                return;
            }
            const formData = new FormData();
            formData.append('file', file);
            this.uploading.push(m);
            await axios.post(`${this.$store.state.apiUrl}/api/rocks/milestone/file`, formData)
                .then(resp => {
                    m.Link = resp.data.Link;
                    this.uploadedLinks.push(resp.data.Link);
                })
                .catch(err => {
                    console.log(err);
                    alert('There was an error uploading that file');
                })
            this.uploading.splice(this.uploading.indexOf(m), 1);
        },
        // What the API saves the link as (rocks.ts milestoneLink): "https://" added when it has no scheme
        linkHref(link: string): string {
            const l = String(link || '').trim();
            return /^https?:\/\//i.test(l) ? l : 'https://' + l;
        },
        // Milestones the Forge drafted (ForgeRockMilestones.vue), already in the form's shape; saved with the rock
        addDrafted(list: any[]) {
            this.form.Milestones.push(...list.map((m: any) => ({ Text: m.Text, OwnerEmployeeID: m.OwnerEmployeeID, DueDate: m.DueDate, Done: false })));
        },
        async save() {
            if(this.saving) {
                return;
            }
            if(!this.form.Name.trim()) {
                alert('Give the rock a name.');
                return;
            }
            if(!this.form.Team) {
                alert('Pick a team for the rock.');
                return;
            }
            this.saving = true;
            await axios.post(`${this.$store.state.apiUrl}/api/rocks/rock`, {
                ...this.form,
                ID: this.existing ? this.existing.ID : null,
                Quarter: this.quarter,
                // So the API can remove the ones the save doesn't keep
                UploadedLinks: this.uploadedLinks
            })
                .then(() => {
                    this.uploadedLinks = [];
                    this.$emit('saved');
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.saving = false;
        },
        // Everything from the rock (owners, team, Team Rock, Done, milestones with their owners and dates) as a new
        // rock in this quarter, on track with nothing checked off. Nothing is saved until Add Rock.
        clone() {
            this.form = {
                ...this.form,
                Name: this.form.Name + ' (Copy)',
                Status: 'on',
                // A team you can add rocks to; otherwise you pick one
                Team: this.teams.includes(this.form.Team) ? this.form.Team : null,
                AdditionalOwnerIDs: [ ...this.form.AdditionalOwnerIDs ],
                // Only teams you can add rocks to
                AdditionalTeams: this.form.AdditionalTeams.filter((t: string) => this.teams.includes(t)),
                Milestones: this.form.Milestones.map((m: any) => ({ Text: m.Text, OwnerEmployeeID: m.OwnerEmployeeID, DueDate: m.DueDate, Done: false, Link: m.Link || null }))
            };
            this.cloned = true;
            this.$nextTick(() => {
                const input = this.$refs.nameInput as HTMLInputElement;
                input?.focus();
                input?.select();
            });
        },
        async carry() {
            await axios.post(`${this.$store.state.apiUrl}/api/rocks/rock/carry`, { ID: this.rock.ID })
                .then(() => {
                    this.$emit('saved');
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
        },
        async remove() {
            this.showConfirmDelete = false;
            await axios.post(`${this.$store.state.apiUrl}/api/rocks/rock/delete`, { ID: this.rock.ID })
                .then(() => {
                    this.$emit('saved');
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
        }
    }
});
</script>

<style scoped>
.rk-modal-title { margin: 0 0 6px; font-size: 20px; }
.rk-label { margin: 18px 0 8px; font-size: 14px; font-weight: bold; }
.rk-done { resize: vertical; min-height: 54px; line-height: 1.45; font-family: inherit; }
.rk-hint { font-weight: normal; color: var(--font-mid-gray); font-size: 12px; }
.rk-teamrock { display: flex; align-items: flex-start; gap: 10px; margin-top: 16px; padding: 11px 14px; border: 1px solid var(--mid-gray); border-radius: 10px; background: #fff; cursor: pointer; transition: .2s all; }
.rk-teamrock:hover:not(.disabled) { border-color: var(--primary-color); }
.rk-teamrock.on { border-color: var(--primary-color); background: var(--light-gray); }
.rk-teamrock.disabled { cursor: default; }
.rk-teamrock input[type=checkbox] { width: 16px !important; height: 16px; margin: 2px 0 0 !important; flex-shrink: 0; accent-color: var(--primary-color); cursor: inherit; }
.rk-teamrock-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.rk-teamrock-title { font-size: 14px; font-weight: bold; line-height: 1.3; }
.rk-row { display: flex; gap: 15px; }
.rk-owners { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.rk-owner-chip { display: inline-flex; align-items: center; gap: 6px; padding: 5px 10px; border: 1px solid var(--mid-gray); border-radius: 14px; background: var(--light-gray); font-size: 13px; }
.rk-owner-x { display: flex; cursor: pointer; }
.rk-owner-x svg { width: 10px; height: 10px; fill: var(--primary-color); }
.rk-owners select.rk-owner-add { width: auto; margin: 0 !important; padding: 5px 10px !important; font-size: 13px !important; }
.rk-row > div { flex: 1; min-width: 0; }
.rk-row select { width: 100%; }
.rk-seg { display: inline-flex; background: #fff; border: 1px solid var(--mid-gray); border-radius: 8px; padding: 2px; }
.rk-seg button { background: none; border: none; font-family: inherit; font-size: 12.5px; color: var(--font-dark-gray); padding: 6px 13px; border-radius: 6px; cursor: pointer; }
.rk-seg button.on { background: var(--primary-color); color: #fff; font-weight: 600; }
.rk-seg button:disabled { cursor: default; }
.rk-ms { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.rk-ms input, .rk-ms select { margin: 0 !important; padding: 7px 9px !important; font-size: 13px !important; }
.rk-ms input[type=checkbox] { width: 16px; height: 16px; flex-shrink: 0; padding: 0 !important; }
.rk-ms-text { flex: 1; min-width: 0; }
/* The checklist drag handle (style.scss) stretches to its row's height; here it centers on the inputs */
.rk-ms .crm-checklist-handle { align-self: center; padding-top: 0; margin-right: -2px; }
.rk-ms.done .rk-ms-text { text-decoration: line-through; color: var(--font-mid-gray); }
.rk-ms-own { width: 130px; flex-shrink: 0; }
.rk-ms-date { width: 140px; flex-shrink: 0; }
.rk-ms-wk { font-size: 11px; color: var(--font-mid-gray); width: 34px; flex-shrink: 0; }
.rk-ms-ln { display: flex; width: 14px; flex-shrink: 0; cursor: pointer; }
.rk-ms-ln svg { width: 14px; height: 14px; fill: var(--font-mid-gray); }
.rk-ms-ln.on svg, .rk-ms-ln:hover svg { fill: var(--primary-color); }
.rk-ms-link { display: flex; align-items: center; gap: 10px; margin: -2px 0 8px 22px; }
.rk-ms-file { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; font-size: 13.5px; color: var(--font-dark-gray); }
.rk-ms-file span { text-decoration: underline; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rk-ms-file svg { width: 13px; height: 13px; fill: var(--primary-color); flex-shrink: 0; }
.rk-ms-file:hover { color: var(--primary-color); }
.rk-ms-link input { flex: 1; min-width: 0; margin: 0 !important; padding: 7px 9px !important; font-size: 13px !important; }
.rk-ms-x { display: flex; cursor: pointer; }
.rk-ms-x svg { width: 12px; height: 12px; fill: var(--primary-color); }
.rk-add-ms { display: inline-block; font-size: 13px; font-weight: 600; cursor: pointer; margin-top: 4px; }
.rk-add-ms:hover { color: var(--primary-color); }
.rk-actions { display: flex; align-items: center; gap: 10px 16px; margin-top: 24px; flex-wrap: wrap; }
.rk-actions .btn, .rk-actions .btn-light { margin: 0; padding: 8px 18px; }
.rk-actions-side { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 16px; flex: 1; min-width: 0; }
.rk-actions-main { display: flex; gap: 10px; flex-shrink: 0; margin-left: auto; }
.rk-link { color: var(--font-dark-gray); font-weight: 600; text-decoration: underline; cursor: pointer; font-size: 13.5px; white-space: nowrap; }
.rk-link:hover { color: var(--primary-color); }
.rk-danger { color: var(--primary-color); text-decoration: underline; cursor: pointer; font-size: 13.5px; white-space: nowrap; }

@media(max-width: 767px) {
    .rk-actions-side { flex-basis: 100%; }
    .rk-actions-main { flex-basis: 100%; justify-content: flex-end; }
    .rk-row { flex-direction: column; gap: 0; }
    .rk-ms { flex-wrap: wrap; }
    .rk-ms-text { flex-basis: calc(100% - 30px); }
    /* Room for the drag handle too, so the text stays on the first line */
    .rk-ms-drag .rk-ms-text { flex-basis: calc(100% - 68px); }
}
</style>
